"use client";
import React, { useEffect, useState, Suspense } from "react";
import dynamic from "next/dynamic";
import "leaflet/dist/leaflet.css";
import { Layer, LeafletMouseEvent, Path } from "leaflet";
import type { Feature, GeoJsonProperties, Geometry } from "geojson";
import { getCountryCoords } from "@/_data/CountryCoords";
import { countryPolygon } from "@/_data/country_polygon";
import Legend from "@/_components/Legend";

// react-leaflet requires dynamic imports - no ssr
const MapContainer = dynamic(
  () => import("react-leaflet").then((m) => m.MapContainer),
  { ssr: false },
);

const Popup = dynamic(() => import("react-leaflet").then((m) => m.Popup), {
  ssr: false,
});

const GeoJSONLayer = dynamic(
  () => import("react-leaflet").then((m) => m.GeoJSON),
  { ssr: false },
);

// Type definitions
interface ApiResult {
  key: string;
  count: number;
}

interface ApiResponse {
  results: ApiResult[];
}

interface CountryData extends ApiResult {
  coords: [number, number];
}

interface PopupContentStyle {
  textAlign: "center";
  fontSize: string;
  verticalAlign: string;
  display: string;
}

interface PopupHeadStyle {
  fontSize: string;
  marginBottom: string;
}

const popupContentStyle: PopupContentStyle = {
  textAlign: "center",
  fontSize: "16px",
  verticalAlign: "middle",
  display: "inline-block",
};

const popupHeadStyle: PopupHeadStyle = {
  fontSize: "18px",
  marginBottom: "5px",
};

// Loading component
function MapLoading(): React.ReactElement {
  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        fontSize: "18px",
      }}
    >
      Loading map...
    </div>
  );
}

function MapComponent(): React.ReactElement {
  const [countryData, setCountryData] = useState<CountryData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [zoom, setZoom] = useState(1.5);
  const [mapHeight, setMapHeight] = useState("80vh");
  const [mapLat, setMapLat] = useState(40);

  useEffect(() => {
    const handleMapResize = () => {
      const width = window.innerWidth;
      console.log("width:", width);

      if (width < 500) {
        setZoom(0.6);
        setMapHeight("45vh");
        setMapLat(10);
      } else if (width < 1024) {
        setZoom(1.2);
        setMapHeight("70vh");
      } else if (width < 1400) {
        setZoom(1.4);
        setMapHeight("80vh");
      } else if (width < 1600) {
        setZoom(1.6);
        setMapHeight("80vh");
      } else if (width < 2048) {
        setZoom(2);
        setMapHeight("80vh");
      } else if (width < 3000) {
        setZoom(2.5);
        setMapHeight("80vh");
      } else {
        setZoom(3);
        setMapHeight("80vh");
      }
    };
    // Set initial map zoom
    handleMapResize();

    // Update on window resize
    window.addEventListener("resize", handleMapResize);
    return () => window.removeEventListener("resize", handleMapResize);
  }, []);

  // States for popup position and selected country
  const [popupPos, setPopupPos] = useState<[number, number] | null>(null);
  const [popupCountryKey, setPopupCountryKey] = useState<string | null>(null);
  const getFeatureCountryKey = (
    feature?: Feature<Geometry, GeoJsonProperties>,
  ) => {
    const p = feature?.properties || {};
    return (
      p.admin ||
      p.ADMIN ||
      p.name_long ||
      p.NAME_LONG ||
      p.name ||
      p.NAME ||
      p.sovereignt ||
      p.SOVEREIGNT ||
      ""
    );
  };

  const onEachFeature = (
    feature: Feature<Geometry, GeoJsonProperties>,
    layer: Layer,
  ) => {
    // Add hover effects and show popup as tooltip
    layer.on("mouseover", (e: LeafletMouseEvent) => {
      const key = getFeatureCountryKey(feature);
      setPopupCountryKey(key || null);
      setPopupPos([e.latlng.lat, e.latlng.lng]);

      (layer as Path).setStyle({
        weight: 1.4,
      });
    });

    layer.on("mouseout", () => {
      // Reset to original style and hide popup
      setPopupPos(null);
      setPopupCountryKey(null);

      const countryKey = getFeatureCountryKey(feature);
      const country = countryData.find((c) => c.key === countryKey);
      const count = country?.count ?? 0;
      const fillColor = getColor(count);

      (layer as Path).setStyle({
        fillColor,
        weight: 1,
        fillOpacity: 1,
      });
    });
    // Click on a country to search for its docs
    layer.on("click", () => {
      const countryKey = getFeatureCountryKey(feature);
      if (countryKey) {
        window.open(
          `${process.env.NEXT_PUBLIC_BASE_URL}/search?filters={"DebtorCountry":["${countryKey}"]}`,
          "_blank",
        );
      }
    });
  };

  const selectedCount = popupCountryKey
    ? (countryData.find((c) => c.key === popupCountryKey)?.count ?? 0)
    : 0;

  const geoJSONStyle = (feature?: Feature<Geometry, GeoJsonProperties>) => {
    const countryKey = getFeatureCountryKey(feature);
    const country = countryData.find((c) => c.key === countryKey);
    const count = country?.count ?? 0;
    const fillColor = getColor(count);

    return {
      fillColor,
      weight: 1,
      opacity: 1,
      color: "#686868",
      fillOpacity: 1,
    };
  };

  useEffect(() => {
    const fetchCountryData = async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await fetch("/api/rollup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            groupBy: "DebtorCountry",
            sort: "count",
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ApiResponse = await response.json();

        // lookup and add coordinates to each country obj
        const dataWithCoords: CountryData[] =
          data.results?.map((country) => ({
            ...country,
            coords: getCountryCoords(country.key),
          })) || [];

        setCountryData(dataWithCoords);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unknown error occurred";
        setError(errorMessage);
        console.error("Error fetching country data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCountryData();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
        }}
      >
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4 justify-center">
        Error loading data: {error}
      </div>
    );
  }

  // Choropleth color scheme 3 - blue shades based on volume - 2.0
  function getColor(count: number) {
    return count > 20
      ? "#00205b"
      : count > 10
        ? "#0055aa"
        : count > 0
          ? "#347bc1"
          : "#b6b9bc";
  }

  const legendItems = [
    { color: "#00205b", label: "20+" },
    { color: "#0055aa", label: "11-20" },
    { color: "#347bc1", label: "1-10" },
    { color: "#b6b9bc", label: "0" },
  ];

  return (
    <MapContainer
      center={[mapLat, 0]}
      zoomSnap={0.1}
      zoom={zoom}
      style={{
        width: "100%",
        height: mapHeight,
        overflow: "hidden",
        padding: "0",
        margin: "0",
        position: "relative",
      }}
      scrollWheelZoom={false}
      zoomControl={false}
      attributionControl={false}
    >
      <GeoJSONLayer
        data={countryPolygon}
        style={geoJSONStyle}
        onEachFeature={onEachFeature}
        interactive
      />
      {/* Standalone popup */}
      {popupPos && (
        <Popup
          className="custom-popup"
          position={popupPos}
          closeButton={false}
          eventHandlers={{
            remove: () => {
              setPopupPos(null);
              setPopupCountryKey(null);
            },
          }}
        >
          <div style={popupContentStyle}>
            <div style={popupHeadStyle}>
              {popupCountryKey || "Selected country"}
            </div>
            {popupCountryKey && (
              <a
                href={`${process.env.NEXT_PUBLIC_BASE_URL}/search?filters={"DebtorCountry":["${popupCountryKey}"]}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span style={{ color: "#00205b", fontWeight: "600" }}>
                  {selectedCount}{" "}
                  {selectedCount === 1 ? "document" : "documents"}
                </span>
              </a>
            )}
          </div>
        </Popup>
      )}
      <Legend categories={legendItems} />
    </MapContainer>
  );
}

// suspense is needed because useSearchParams() can cause dynamic rendering,
// and it provides a fallback ui while waiting for load
export default function Page(): React.ReactElement {
  return (
    <>
      <style jsx global>{`
        html {
          --body-min-height: auto;
        }
        // set background color for Map Container
        .leaflet-container {
          background-color: #ffffff !important;
        }

        // Hide map vertical scrollbar
        .leaflet-container::-webkit-scrollbar {
          display: none;
        }
        .leaflet-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        // Add country hover effect
        .leaflet-interactive:hover {
          filter: drop-shadow(0 0 4px #111f2d);
        }
        .custom-popup .leaflet-popup-content {
          font-weight: 600;
        }
      `}</style>
      <Suspense fallback={<MapLoading />}>
        <MapComponent />
      </Suspense>
    </>
  );
}
